

import React, { useState, useEffect, useCallback, useRef } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Pencil, Trash2, Plus, Search, ChevronLeft, ChevronRight, 
  MapPin, Calendar, ArrowLeft, Save, 
  Loader2, User, X, Check, Eye, Users, Info, Phone, Mail, RotateCcw
} from "lucide-react";

const HolidayManager = () => {
  // --- VIEW NAVIGATION ---
  const [view, setView] = useState("list"); 
  
  // --- DATA STATES ---
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // --- MODAL & SEARCH STATES ---
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [memberSearch, setMemberSearch] = useState(""); 
  const [memberResults, setMemberResults] = useState([]); 
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const dropdownRef = useRef(null);

  // --- FORM STATES ---
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    memberId: "", 
    destination: "", 
    checkIn: "", 
    checkOut: "", 
    adults: 2, 
    kids: 0, 
    status: "Pending",
    comment: "" // Added field
  });

  // --- FETCHING LOGIC (Defined first to avoid ReferenceErrors) ---
  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.HOLIDAY_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error("Fetch failed with status:", response.status);
        setHolidays([]);
        setLoading(false);
        return;
      }

      const result = await response.json();
      setHolidays(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { 
      console.error("Holiday fetch error:", e); 
      setHolidays([]);
    }
    setLoading(false);
  }, [currentPage, searchTerm]);

  const fetchMemberSuggestions = async (query = "") => {
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_GET_ALL}?pageNumber=1&pageSize=10&searchTerm=${query}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      setMemberResults(data.data || []); 
    } catch (err) { console.error(err); }
  };

  // --- EFFECTS ---
  useEffect(() => {
    if (view === "list") fetchHolidays();
  }, [view, fetchHolidays]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowMemberDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- HANDLERS ---
  const handleSearchTrigger = () => { 
    setSearchTerm(inputValue); 
    setCurrentPage(1); 
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleOpenCreate = () => {
    setEditingId(null); setSelectedMemberName(""); setMemberSearch("");
    setFormData({ 
      memberId: "", 
      destination: "", 
      checkIn: "", 
      checkOut: "", 
      adults: 2, 
      kids: 0, 
      status: "Pending", 
      comment: "" // Added field reset
    });
    setView("manage");
  };

  const handleOpenEdit = (h) => {
    setEditingId(h.holidayId);
    setSelectedMemberName(h.member?.memberName || ""); 
    setFormData({ ...h, checkIn: h.checkIn ? h.checkIn.split('T')[0] : "", checkOut: h.checkOut ? h.checkOut.split('T')[0] : "" });
    setView("manage");
  };

  const selectMember = (m) => {
    setFormData({ ...formData, memberId: m.memberId });
    setSelectedMemberName(m.memberName); 
    setShowMemberDropdown(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if(!formData.memberId) return Swal.fire("Required", "Please select a member.", "warning");
    setSaving(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.HOLIDAY_BASE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, holidayId: editingId }),
      });
      if (res.ok) { setView("list"); Swal.fire("Success", "Saved successfully", "success"); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete?', icon: 'warning', showCancelButton: true });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.HOLIDAY_BASE}/${id}`, { method: "DELETE" });
      fetchHolidays();
    }
  };

  // --- STYLES ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "35px" },
    title: { fontSize: "30px", fontWeight: "900", color: "#1b2559", margin: 0 },
    tableWrapper: { backgroundColor: "#fff", borderRadius: "20px", border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" },
    th: { padding: "18px 24px", backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", textAlign: "left" },
    td: { padding: "20px 24px", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
    input: { width: "100%", padding: "14px 16px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "14px", boxSizing: "border-box" },
    label: { display: "block", fontSize: "12px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: "uppercase" },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", padding: "12px 24px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    btnGhost: { backgroundColor: "#fff", color: "#64748b", padding: "12px 24px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
    badge: (s) => ({ backgroundColor: s === "Approved" ? "#dcfce7" : "#fef3c7", color: s === "Approved" ? "#166534" : "#92400e", padding: "5px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "800" }),
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
    modalContent: { backgroundColor: "#fff", borderRadius: "28px", width: "550px", padding: "40px", boxShadow: "0 25px 50px rgba(0,0,0,0.15)" },
    detailRow: { display: "flex", alignItems: "center", gap: "15px", padding: "15px 0", borderBottom: "1px solid #f1f5f9" },
    detailLabel: { width: "120px", color: "#a3aed0", fontWeight: "700", fontSize: "12px", textTransform: "uppercase" },
    detailValue: { flex: 1, color: "#1b2559", fontWeight: "700", fontSize: "15px" },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, border: '1px solid #e2e8f0', marginTop: '5px', maxHeight: '200px', overflowY: 'auto' },
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {view === "list" ? (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={styles.header}>
              <div>
                <h1 style={styles.title}>Holiday</h1>
              </div>
              <button style={styles.btnPrimary} onClick={handleOpenCreate}><Plus size={20}/> New Holiday</button>
            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "30px" }}>
              <div style={{ position: "relative", flex: 1, maxWidth: "450px" }}>
                <Search size={18} style={{ position: "absolute", left: "15px", top: "15px", color: "#cbd5e1" }} />
                <input style={{ ...styles.input, paddingLeft: "45px" }} placeholder="Search name or destination..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchTrigger()} />
              </div>
              <button style={styles.btnPrimary} onClick={handleSearchTrigger}>Search</button>
              {searchTerm && (
                 <button style={styles.btnGhost} onClick={handleClearSearch}>
                    <RotateCcw size={16}/> Clear
                 </button>
              )}
            </div>

            <div style={styles.tableWrapper}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: "25%" }}>Member Name</th>
                    <th style={{ ...styles.th, width: "20%" }}>Destination</th>
                    <th style={{ ...styles.th, width: "20%" }}>Travel Date</th>
                    <th style={{ ...styles.th, width: "15%" }}>Guests</th>
                    <th style={{ ...styles.th, width: "10%" }}>Status</th>
                    <th style={{ ...styles.th, width: "10%", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" style={{ padding: "80px", textAlign: "center" }}><Loader2 className="animate-spin" /></td></tr>
                  ) : (holidays.length === 0 ? (
                    <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: '#a3aed0' }}>No records found. Check API logs if this is unexpected.</td></tr>
                  ) : holidays.map(h => (
                    <tr key={h.holidayId}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: "800", color: "#1b2559", fontSize: "15px" }}>{h.member?.memberName}</div>
                        <div style={{ fontSize: "12px", color: "#a3aed0" }}>{h.member?.phoneNo}</div>
                      </td>
                      <td style={styles.td}><div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "700", color: "#4f46e5" }}><MapPin size={14}/> {h.destination}</div></td>
                      <td style={styles.td}><div style={{ fontSize: "13px", fontWeight: "600", color: "#1b2559" }}>{new Date(h.checkIn).toLocaleDateString('en-GB')} — {new Date(h.checkOut).toLocaleDateString('en-GB')}</div></td>
                      <td style={styles.td}><div style={{ display: "flex", alignItems: "center", gap: "5px", color: "#64748b", fontWeight: "700" }}><Users size={15}/> {h.adults}A, {h.kids}K</div></td>
                      <td style={styles.td}><span style={styles.badge(h.status)}>{h.status}</span></td>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                         <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                           <button onClick={() => { setViewData(h); setIsViewModalOpen(true); }} style={{ border: "none", background: "#f1f5f9", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Eye size={16}/></button>
                           <button onClick={() => handleOpenEdit(h)} style={{ border: "none", background: "#eef2ff", color: "#4f46e5", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Pencil size={16}/></button>
                           <button onClick={() => handleDelete(h.holidayId)} style={{ border: "none", background: "#fef2f2", color: "#ef4444", padding: "8px", borderRadius: "8px", cursor: "pointer" }}><Trash2 size={16}/></button>
                         </div>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
              <div style={{ padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "13px", color: "#a3aed0", fontWeight: "700" }}>Page {currentPage} of {totalPages}</span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnGhost}><ChevronLeft size={18}/></button>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnGhost}><ChevronRight size={18}/></button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ================= MANAGE FORM ================= */
          <div style={{ animation: "slideUp 0.4s ease" }}>
            <button style={{ ...styles.btnGhost, marginBottom: "30px" }} onClick={() => setView("list")}><ArrowLeft size={18}/> Back to List</button>
            <div style={{ maxWidth: "800px", margin: "0 auto", backgroundColor: "#fff", padding: "40px", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
              <h2 style={{ fontSize: "24px", fontWeight: "900", marginBottom: "30px", color: "#1b2559" }}>{editingId ? "Edit Assignment" : "Assign New Holiday"}</h2>
              <form onSubmit={handleFormSubmit}>
                
                <div style={{ marginBottom: "25px", position: "relative" }} ref={dropdownRef}>
                  <label style={styles.label}>Select Member *</label>
                  <div style={{ position: "relative" }}>
                    <User size={18} style={{ position: "absolute", left: "15px", top: "14px", color: "#cbd5e1" }} />
                    <input style={{ ...styles.input, paddingLeft: "45px" }} placeholder="Search all members..." value={selectedMemberName || memberSearch} onFocus={() => { setShowMemberDropdown(true); fetchMemberSuggestions(""); }} onChange={(e) => { setSelectedMemberName(""); setMemberSearch(e.target.value); fetchMemberSuggestions(e.target.value); }} />
                  </div>
                  {showMemberDropdown && (
                    <div style={styles.dropdown}>
                      {memberResults.map(m => (
                        <div key={m.memberId} style={{ padding: "12px 20px", cursor: "pointer", borderBottom: "1px solid #f8fafc" }} onClick={() => selectMember(m)}>
                          <strong style={{color: "#1b2559"}}>{m.memberName}</strong> <span style={{fontSize:'12px', color:'#a3aed0'}}>({m.phoneNo})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "25px" }}>
                  <label style={styles.label}>Destination *</label>
                  <input style={styles.input} required value={formData.destination} onChange={(e) => setFormData({ ...formData, destination: e.target.value })} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "25px", marginBottom: "25px" }}>
                  <div><label style={styles.label}>Check-In Date *</label><input type="date" style={styles.input} required value={formData.checkIn} onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })} /></div>
                  <div><label style={styles.label}>Check-Out Date *</label><input type="date" style={styles.input} required value={formData.checkOut} onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })} /></div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "25px", marginBottom: "25px" }}>
                  <div><label style={styles.label}>Adults</label><input type="number" style={styles.input} value={formData.adults} onChange={(e) => setFormData({ ...formData, adults: e.target.value })} /></div>
                  <div><label style={styles.label}>Kids</label><input type="number" style={styles.input} value={formData.kids} onChange={(e) => setFormData({ ...formData, kids: e.target.value })} /></div>
                  <div>
                    <label style={styles.label}>Status</label>
                    <select style={styles.input} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Added Comment Field */}
                <div style={{ marginBottom: "40px" }}>
                  <label style={styles.label}>Comments / Notes</label>
                  <textarea 
                    style={{ ...styles.input, height: "80px", resize: "none" }} 
                    placeholder="Add any special requests or notes here..."
                    value={formData.comment || ""} 
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })} 
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px", borderTop: "1px solid #f1f5f9", paddingTop: "30px" }}>
                   <button type="button" style={{ ...styles.btnGhost, padding: "12px 35px" }} onClick={() => setView("list")}>Cancel</button>
                   <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, padding: "12px 40px" }}>
                     {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} {saving ? "Saving..." : "Confirm Booking"}
                   </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ================= VIEW MODAL ================= */}
        {isViewModalOpen && viewData && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                <h3 style={{ fontSize: "22px", fontWeight: "900", margin: 0, color: "#1b2559" }}>Holiday Details</h3>
                <button onClick={() => setIsViewModalOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", color: "#cbd5e1" }}><X size={24}/></button>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Member</div>
                  <div style={styles.detailValue}>{viewData.member?.memberName}</div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Contact Info</div>
                  <div style={styles.detailValue}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}><Phone size={14}/> {viewData.member?.phoneNo}</div>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Email Address</div>
                  <div style={styles.detailValue}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', fontSize:'13px'}}><Mail size={14}/> {viewData.member?.email || 'Not Provided'}</div>
                  </div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Destination</div>
                  <div style={{ ...styles.detailValue, color: "#4f46e5" }}>{viewData.destination}</div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Window</div>
                  <div style={styles.detailValue}>{new Date(viewData.checkIn).toLocaleDateString('en-GB')} — {new Date(viewData.checkOut).toLocaleDateString('en-GB')}</div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Guests</div>
                  <div style={styles.detailValue}>{viewData.adults} Adults, {viewData.kids} Kids</div>
                </div>
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Status</div>
                  <div style={styles.detailValue}><span style={styles.badge(viewData.status)}>{viewData.status}</span></div>
                </div>
                {/* Added Comment display in View */}
                <div style={styles.detailRow}>
                  <div style={styles.detailLabel}>Comments</div>
                  <div style={{ ...styles.detailValue, fontWeight: "500", fontSize: "14px", color: "#64748b" }}>
                    {viewData.comment || 'No comments provided'}
                  </div>
                </div>
              </div>

              <button onClick={() => setIsViewModalOpen(false)} style={{ ...styles.btnPrimary, width: "100%", justifyContent: "center", marginTop: "35px" }}>Close Summary</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HolidayManager;